import i18next from 'i18next';
import React from 'react';

import { AudiencesIcon, DashboardIcon, OrganizationIcon } from '~/assets';
import AIInsightsIconNew from '~/assets/AIInsightsIconNew';
import { conversationsPath, homeAudiencesPath, homePath } from '~/constants';
import { AudienceCondition, OrganizationType } from '~/types/api.graphql';
import { allConditionsArray, conditionIcon, conditionTitle, getCreatePathForCondition } from '~/utils/audience';
import { mapOrgTypeIcons } from '~/utils/organization';
import { SideBarContent } from '~/views/layouts/SideBar/SideBar';

const shouldShowSegmentsInMainList = process.env.REACT_APP_SHOW_SEGMENTS_IN_MAIN_LIST === 'true';

const getConditions = () => {
  return shouldShowSegmentsInMainList
    ? allConditionsArray
    : allConditionsArray.filter((condition) => condition !== AudienceCondition.Segment);
};

const buildDashboardAndAiInsightsItems = (conversationId: string | null) => ({
  items: [
    {
      title: i18next.t('dashboard.title'),
      icon: <DashboardIcon />,
      path: homePath,
    },
    {
      title: i18next.t('sideBar.aiChat'),
      icon: <AIInsightsIconNew />,
      path: conversationId ? `${conversationsPath}/${conversationId}` : conversationsPath,
    },
  ],
});

export const mainSidebar = (
  orgTypes: OrganizationType[] = [],
  hasAudienceOnlyAccess: boolean,
  hasInviteToAnOrganization: boolean,
  hasAccessToAudience: boolean,
  loading = false,
  conversationId: string | null = null,
): SideBarContent => ({
  name: 'main',
  sections: [
    ...[buildDashboardAndAiInsightsItems(conversationId)],
    /* eslint-disable @typescript-eslint/indent */
    ...(hasAccessToAudience || hasAudienceOnlyAccess
      ? [
          {
            title: i18next.t('specific:audiences.title'),
            collapsedProps: {
              title: i18next.t('audiences.titleShort'),
            },
            items: [
              {
                title: i18next.t('specific:audiences.allAudiencesTitle'),
                icon: <AudiencesIcon />,
                path: homeAudiencesPath,
                defaultSelected: true,
                loading,
                navigateOptions: {
                  state: { audienceConditionFilterId: undefined },
                },
              },
              ...getConditions()
                .filter((condition) =>
                  condition === AudienceCondition.Lookalike ? process.env.REACT_APP_LOOKALIKE_ENABLED === 'true' : true,
                )
                .map((condition) => {
                  const createPath = getCreatePathForCondition(condition);
                  return {
                    title: conditionTitle(condition),
                    icon: conditionIcon(condition),
                    path: homeAudiencesPath,
                    loading,
                    navigateOptions: {
                      state: { audienceConditionFilterId: condition },
                    },
                    badge: condition === AudienceCondition.Lookalike ? i18next.t('newLookalikeAudience.ai') : undefined,
                    createPath,
                  };
                }),
            ],
          },
        ]
      : []),
    /* eslint-disable @typescript-eslint/indent */
    ...(loading || !hasAudienceOnlyAccess || hasInviteToAnOrganization
      ? [
          {
            title: i18next.t('organizations.title'),
            collapsedProps: {
              title: i18next.t('organizations.titleShort'),
            },
            items: [
              {
                title: i18next.t('organizations.allOrganizationsTitle'),
                icon: <OrganizationIcon />,
                path: '/app/organizations',
                defaultSelected: true,
                loading,
                navigateOptions: { state: { organizationType: undefined } },
              },
              ...orgTypes.map((organizationType) => ({
                title: i18next.t(`organization.settings.orgTypes.${organizationType.toLowerCase()}`),
                icon: mapOrgTypeIcons[organizationType],
                path: '/app/organizations',
                loading,
                navigateOptions: { state: { organizationType } },
              })),
            ],
          },
        ]
      : []),
    /* eslint-enable @typescript-eslint/indent */
  ],
});
